import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface CloudConnection {
  provider: string;
  connected: boolean;
  accessToken?: string;
  cloudAccountId?: string;
  accountId?: string;
  subscriptionId?: string;
  subscriptionIds?: string[];
  cloudAccountIds?: string[];
  connectedAt?: string;
}

interface CloudContextType {
  connections: Record<string, CloudConnection>;
  setConnection: (provider: string, connection: CloudConnection) => void;
  isConnected: (provider: string) => boolean;
  getConnection: (provider: string) => CloudConnection | null;
}

const CloudContext = createContext<CloudContextType | undefined>(undefined);

export const useCloud = (): CloudContextType => {
  const context = useContext(CloudContext);
  if (!context) {
    throw new Error('useCloud must be used within a CloudProvider');
  }
  return context;
};

interface CloudProviderProps {
  children: ReactNode;
}

export const CloudProvider: React.FC<CloudProviderProps> = ({ children }) => {
  const [connections, setConnections] = useState<Record<string, CloudConnection>>({});

  // Carregar conexões do localStorage
  useEffect(() => {
    const savedConnections = localStorage.getItem('cloudConnections');
    if (savedConnections) {
      try {
        setConnections(JSON.parse(savedConnections));
      } catch (error) {
        console.error('Erro ao carregar conexões:', error);
      }
    }

    const clearConnections = () => setConnections({});
    window.addEventListener('cloud:clear', clearConnections);
    return () => window.removeEventListener('cloud:clear', clearConnections);
  }, []);

  // Salvar conexões no localStorage
  useEffect(() => {
    localStorage.setItem('cloudConnections', JSON.stringify(connections));
  }, [connections]);

  const setConnection = (provider: string, connection: CloudConnection) => {
    setConnections(prev => ({
      ...prev,
      [provider]: {
        ...connection,
        connectedAt: new Date().toISOString()
      }
    }));
  };

  const isConnected = (provider: string): boolean => {
    return connections[provider]?.connected || false;
  };

  const getConnection = (provider: string): CloudConnection | null => {
    return connections[provider] || null;
  };

  const value: CloudContextType = {
    connections,
    setConnection,
    isConnected,
    getConnection
  };

  return (
    <CloudContext.Provider value={value}>
      {children}
    </CloudContext.Provider>
  );
};
